/**
 * useWorkOrderSections Hook
 *
 * Component → Hook → API layer for a work order's granular sections
 * (notes, checklists, before/progress/after photos) and the per-section
 * admin toggles. Wraps `workOrdersApi`, manages submitting/error state, and
 * refetches the work order after each mutation (one-time get() reads + refetch).
 *
 * The caller owns the source-of-truth work order (loaded on the detail page);
 * this hook performs the writes and asks the caller to refetch via `onChanged`.
 */
import { useCallback, useState } from 'react';
import {
    workOrdersApi,
    WorkOrderSection,
    WorkOrderPhotoType
} from '../../api/workOrdersApi';
import { WorkOrderWithBuilding } from '../../types/workOrders';

interface UseWorkOrderSectionsProps {
    buildingKey: string;
    workOrderKey: string;
    /** Called after a successful mutation so the caller can refetch the work order.
     *  Callers should make this a SILENT refetch (no loading flag) so the detail
     *  view updates in place rather than reloading. */
    onChanged: () => Promise<void> | void;
    /** Apply an optimistic update to the caller's work-order state, avoiding a full
     *  refetch/reload. Used for cheap mutations like the section toggles so flipping
     *  a switch updates state immediately without reloading the page. */
    applyLocalUpdate?: (updater: (workOrder: WorkOrderWithBuilding) => WorkOrderWithBuilding) => void;
}

export interface UseWorkOrderSectionsReturn {
    submitting: boolean;
    error: string | null;
    clearError: () => void;

    // Section toggles (admin)
    setSectionEnabled: (section: WorkOrderSection, enabled: boolean) => Promise<void>;

    // Notes
    addNote: (title: string, content: string) => Promise<void>;
    updateNote: (noteKey: string, title: string, content: string) => Promise<void>;
    deleteNote: (noteKey: string) => Promise<void>;

    // Checklists
    addChecklist: (title: string) => Promise<void>;
    updateChecklistTitle: (checklistKey: string, title: string) => Promise<void>;
    deleteChecklist: (checklistKey: string) => Promise<void>;
    addChecklistItem: (checklistKey: string, text: string) => Promise<void>;
    updateChecklistItemText: (checklistKey: string, itemKey: string, text: string) => Promise<void>;
    toggleChecklistItem: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    deleteChecklistItem: (checklistKey: string, itemKey: string) => Promise<void>;
    reorderChecklistItems: (checklistKey: string, orderedItemKeys: string[]) => Promise<void>;

    // Photos
    uploadPhotos: (photoType: WorkOrderPhotoType, files: File[]) => Promise<void>;
    deletePhoto: (photoType: WorkOrderPhotoType, photoKey: string) => Promise<void>;
}

export const useWorkOrderSections = ({
    buildingKey,
    workOrderKey,
    onChanged,
    applyLocalUpdate
}: UseWorkOrderSectionsProps): UseWorkOrderSectionsReturn => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    // Wrap an action with submitting state, error capture and a refetch.
    const run = useCallback(
        async <T,>(
            action: () => Promise<T>,
            errorMessage: string,
            shouldRefetch = true
        ): Promise<void> => {
            setSubmitting(true);
            setError(null);
            try {
                await action();
                if (shouldRefetch) {
                    await onChanged();
                }
            } catch (err) {
                console.error(errorMessage, err);
                setError(err instanceof Error ? err.message : errorMessage);
                throw err;
            } finally {
                setSubmitting(false);
            }
        },
        [onChanged]
    );

    // ============ SECTION TOGGLES ============

    const setSectionEnabled = useCallback(
        async (section: WorkOrderSection, enabled: boolean) => {
            setError(null);
            // Optimistic local update so the switch flips immediately and the detail
            // view does not reload. Reconcile from the server only if the write fails.
            if (applyLocalUpdate) {
                applyLocalUpdate(wo => ({
                    ...wo,
                    enabledSections: { ...(wo.enabledSections || {}), [section]: enabled }
                }));
            }
            setSubmitting(true);
            try {
                await workOrdersApi.setSectionEnabled(workOrderKey, section, enabled);
                if (!applyLocalUpdate) {
                    await onChanged();
                }
            } catch (err) {
                console.error('Kunde inte uppdatera sektionen', err);
                setError(err instanceof Error ? err.message : 'Kunde inte uppdatera dokumentationen');
                await onChanged(); // reconcile (rolls back the optimistic flip)
            } finally {
                setSubmitting(false);
            }
        },
        [workOrderKey, applyLocalUpdate, onChanged]
    );

    // ============ NOTES ============

    const addNote = useCallback(
        async (title: string, content: string) => {
            await run(
                () => workOrdersApi.addNote(workOrderKey, title, content),
                'Kunde inte lägga till anteckning'
            );
        },
        [run, workOrderKey]
    );

    const updateNote = useCallback(
        async (noteKey: string, title: string, content: string) => {
            await run(
                () => workOrdersApi.updateNote(workOrderKey, noteKey, title, content),
                'Kunde inte uppdatera anteckning'
            );
        },
        [run, workOrderKey]
    );

    const deleteNote = useCallback(
        async (noteKey: string) => {
            await run(
                () => workOrdersApi.deleteNote(workOrderKey, noteKey),
                'Kunde inte radera anteckning'
            );
        },
        [run, workOrderKey]
    );

    // ============ CHECKLISTS ============

    const addChecklist = useCallback(
        async (title: string) => {
            await run(
                () => workOrdersApi.addChecklist(workOrderKey, title),
                'Kunde inte lägga till checklista'
            );
        },
        [run, workOrderKey]
    );

    const updateChecklistTitle = useCallback(
        async (checklistKey: string, title: string) => {
            await run(
                () => workOrdersApi.updateChecklistTitle(workOrderKey, checklistKey, title),
                'Kunde inte uppdatera checklistatitel'
            );
        },
        [run, workOrderKey]
    );

    const deleteChecklist = useCallback(
        async (checklistKey: string) => {
            await run(
                () => workOrdersApi.deleteChecklist(workOrderKey, checklistKey),
                'Kunde inte radera checklista'
            );
        },
        [run, workOrderKey]
    );

    const addChecklistItem = useCallback(
        async (checklistKey: string, text: string) => {
            await run(
                () => workOrdersApi.addChecklistItem(workOrderKey, checklistKey, text),
                'Kunde inte lägga till checklistpunkt'
            );
        },
        [run, workOrderKey]
    );

    const updateChecklistItemText = useCallback(
        async (checklistKey: string, itemKey: string, text: string) => {
            await run(
                () =>
                    workOrdersApi.updateChecklistItemText(
                        workOrderKey,
                        checklistKey,
                        itemKey,
                        text
                    ),
                'Kunde inte uppdatera checklistpunkt'
            );
        },
        [run, workOrderKey]
    );

    const toggleChecklistItem = useCallback(
        async (checklistKey: string, itemKey: string, completed: boolean) => {
            await run(
                () =>
                    workOrdersApi.toggleChecklistItem(
                        workOrderKey,
                        checklistKey,
                        itemKey,
                        completed
                    ),
                'Kunde inte uppdatera checklistpunkt'
            );
        },
        [run, workOrderKey]
    );

    const deleteChecklistItem = useCallback(
        async (checklistKey: string, itemKey: string) => {
            await run(
                () => workOrdersApi.deleteChecklistItem(workOrderKey, checklistKey, itemKey),
                'Kunde inte radera checklistpunkt'
            );
        },
        [run, workOrderKey]
    );

    const reorderChecklistItems = useCallback(
        async (checklistKey: string, orderedItemKeys: string[]) => {
            await run(
                () =>
                    workOrdersApi.reorderChecklistItems(
                        workOrderKey,
                        checklistKey,
                        orderedItemKeys
                    ),
                'Kunde inte ändra ordning'
            );
        },
        [run, workOrderKey]
    );

    // ============ PHOTOS ============

    const uploadPhotos = useCallback(
        async (photoType: WorkOrderPhotoType, files: File[]) => {
            if (files.length === 0) return;
            await run(async () => {
                const results = await Promise.allSettled(
                    files.map(file =>
                        workOrdersApi.uploadWorkOrderPhoto(
                            buildingKey,
                            workOrderKey,
                            photoType,
                            file
                        )
                    )
                );
                const failures = results.filter(
                    r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
                );
                if (failures.length > 0) {
                    throw new Error(
                        failures.length === files.length
                            ? 'Kunde inte ladda upp foton'
                            : `${failures.length} av ${files.length} foton kunde inte laddas upp`
                    );
                }
            }, 'Kunde inte ladda upp foton');
        },
        [run, buildingKey, workOrderKey]
    );

    const deletePhoto = useCallback(
        async (photoType: WorkOrderPhotoType, photoKey: string) => {
            await run(
                () => workOrdersApi.deleteWorkOrderPhoto(workOrderKey, photoType, photoKey),
                'Kunde inte radera foto'
            );
        },
        [run, workOrderKey]
    );

    return {
        submitting,
        error,
        clearError,
        setSectionEnabled,
        addNote,
        updateNote,
        deleteNote,
        addChecklist,
        updateChecklistTitle,
        deleteChecklist,
        addChecklistItem,
        updateChecklistItemText,
        toggleChecklistItem,
        deleteChecklistItem,
        reorderChecklistItems,
        uploadPhotos,
        deletePhoto
    };
};
